import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { EmbedLink } from '../../../../../../components';
import {
  storyCardApi,
  StoryCardArticleParsedUrl,
  StoryCardParseUrlPayload,
  StoryCardQuoteParsedUrl,
  StoryCardType,
} from '../../../../../../services';
import { useAppDispatch } from '../../../../../hooks';
import { isUrl } from '../../../../../utils';
import { InputField } from '../../../../InputField';
import { CloseWrapper, useBlockToolInputFocus } from '../../helpers';

interface ProcessDataArgs {
  url: string;
  parsedData: StoryCardParseUrlPayload;
}

export const EmbedLinkNodeView: FunctionComponent<NodeViewProps> = ({
  editor,
  node,
  getPos,
  updateAttributes,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { onBlockInputFocus } = useBlockToolInputFocus({ editor, getPos });

  const [loading, setLoading] = useState<boolean>(false);

  const [invalidUrl, setInvalidUrl] = useState<boolean>(false);

  const { link } = node.attrs;

  const processData = useCallback(
    ({ url, parsedData }: ProcessDataArgs) => {
      const { type } = parsedData;

      if (![StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type)) {
        setInvalidUrl(true);
        return;
      }

      const image = parsedData.gallery[0]?.image;

      switch (type) {
        case StoryCardType.ARTICLE: {
          const { title, abstract, sourceName } = parsedData as StoryCardArticleParsedUrl;

          return updateAttributes({
            link: {
              url,
              title,
              sourceName,
              text: abstract,
              ...(image && { image }),
            },
          });
        }
        case StoryCardType.QUOTE: {
          const { quote, quotePerson, quotePersonHandle, quoteSource } =
            parsedData as StoryCardQuoteParsedUrl;

          return updateAttributes({
            link: {
              url,
              quoteSource,
              title: quotePerson,
              text: quote,
              sourceName: quotePersonHandle,
              ...(image && { image }),
            },
          });
        }
      }
    },
    [updateAttributes]
  );

  const onPaste = useCallback(
    async (event: React.ClipboardEvent<HTMLInputElement>) => {
      const { clipboardData } = event;

      const value = clipboardData.getData('Text');

      setInvalidUrl(false);

      if (!value) {
        return;
      }

      if (!isUrl(value)) {
        setInvalidUrl(true);
        return;
      }

      setLoading(true);

      const parsedData = await dispatch(
        storyCardApi.endpoints.storyCardParseUrl.initiate({ url: value })
      ).unwrap();

      !parsedData ? setInvalidUrl(true) : processData({ url: value, parsedData });

      setLoading(false);
    },
    [dispatch, processData]
  );

  const content = useMemo(() => {
    if (loading) {
      return <Skeleton height={'3rem'} />;
    }

    if (link) {
      return <EmbedLink link={link} />;
    }

    return (
      <InputField
        value={''}
        placeholder={'https://'}
        onPaste={onPaste}
        onFocus={onBlockInputFocus}
        error={invalidUrl}
        errorMessage={t('common.url-invalid')}
      />
    );
  }, [invalidUrl, link, loading, onBlockInputFocus, onPaste, t]);

  return (
    <NodeViewWrapper as={'div'}>
      <CloseWrapper editor={editor} node={node} getPos={getPos}>
        {content}
      </CloseWrapper>
    </NodeViewWrapper>
  );
};
